import axios from 'axios'
import { MessageBox, Message } from 'element-ui'
import store from '@/store'
import { getToken } from '@/utils/auth'
import { syncRequestContextCookies } from '@/utils/context-cookie'
import { t } from '@/utils/i18n-helper'

const SUCCESS_CODES = [200, 20000]
const AUTH_ERROR_CODES = [401, 50008, 50012, 50014]

// create an axios instance
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // url = base url + request url
  withCredentials: true, // send cookies when cross-domain requests
  timeout: 5000 // request timeout
})

function translateMessage(message, fallback) {
  return t(message, fallback || message || t('request.error', 'Error'))
}

function translateBackendMessage(message, fallback) {
  if (!message) {
    return fallback || t('request.error', 'Error')
  }

  return t(message, message)
}

function normalizeCode(code) {
  const numberCode = Number(code)
  return Number.isNaN(numberCode) ? code : numberCode
}

function getResponseMessage(res, fallback) {
  return translateBackendMessage(res && (res.message || res.errorMessage), fallback)
}

function createRequestError(res, message) {
  const error = new Error(message)
  error.code = res && res.code
  error.data = res
  return error
}

function redirectToLogin() {
  return store.dispatch('user/resetToken').then(() => {
    const currentPath = window.location.pathname + window.location.search
    window.location.href = `/#/login?redirect=${encodeURIComponent(currentPath)}`
  })
}

function handleUnauthorized(message) {
  Message({
    message,
    type: 'error',
    duration: 3 * 1000
  })
  return redirectToLogin()
}

// request interceptor
service.interceptors.request.use(
  config => {
    // do something before request is sent
    syncRequestContextCookies(store.getters.language)

    if (store.getters.token) {
      // let each request carry token
      // use standard Authorization header with Bearer token
      config.headers['Authorization'] = getToken()
    }
    return config
  },
  error => {
    // do something with request error
    console.log(error) // for debug
    return Promise.reject(new Error(translateMessage(error.message, 'Error')))
  }
)

// response interceptor
service.interceptors.response.use(
  /**
   * If you want to get http information such as headers or status
   * Please return  response => response
  */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code
   */
  response => {
    const res = response.data
    const code = normalizeCode(res && res.code)

    if (res && (SUCCESS_CODES.includes(code) || res.success === true)) {
      return res
    }

    if (AUTH_ERROR_CODES.includes(code)) {
      const message = getResponseMessage(res, t('user.expired', 'Your login has expired. Please log in again.'))
      Message({
        message,
        type: 'error',
        duration: 3 * 1000
      })

      if (code === 401) {
        redirectToLogin()
      } else {
        MessageBox.confirm('You have been logged out, you can cancel to stay on this page, or log in again', 'Confirm logout', {
          confirmButtonText: 'Re-Login',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }).then(() => {
          store.dispatch('user/resetToken').then(() => {
            location.reload()
          })
        })
      }

      return Promise.reject(createRequestError(res, message))
    }

    const message = getResponseMessage(res, code === 500 ? t('request.serverError', 'Server error') : t('request.error', 'Error'))
    Message({
      message,
      type: 'error',
      duration: 5 * 1000
    })

    return Promise.reject(createRequestError(res, message))
  },
  error => {
    console.log('err' + error) // for debug
    // 处理 401 错误 - 未授权，跳转到登录页
    if (error.response && error.response.status === 401) {
      handleUnauthorized(t('user.expired', 'Your login has expired. Please log in again.'))
    } else {
      const message = translateMessage(error.message, t('request.networkError', 'Network error'))
      Message({
        message,
        type: 'error',
        duration: 5 * 1000
      })
      error.message = message
    }
    return Promise.reject(error)
  }
)

export default service
